import * as Sentry from "@sentry/react";
import { logger } from "./logger";

// Global error interceptor to catch all errors including dynamic ones
export function setupErrorInterceptor() {
  // Intercept all console errors
  const originalConsoleError = console.error;
  console.error = (...args: any[]) => {
    // Check if any argument contains "strategies" or "Failed to load"
    const errorString = args.map((arg) => String(arg)).join(" ");

    if (
      errorString.toLowerCase().includes("strategies") ||
      errorString.toLowerCase().includes("failed to load")
    ) {
      // Capture to Sentry with enhanced context
      Sentry.captureMessage(`Strategy Loading Error: ${errorString}`, {
        level: "error",
        tags: {
          component: "unknown",
          errorType: "strategy_loading",
          intercepted: true,
        },
        contexts: {
          error_details: {
            arguments: args,
            stackTrace: new Error().stack,
            timestamp: new Date().toISOString(),
            location: window.location.href,
          },
        },
      });

      // Log with enhanced details
      logger.error("🚨 STRATEGY LOADING ERROR INTERCEPTED", {
        errorString,
        arguments: args,
        location: window.location.href,
        timestamp: new Date().toISOString(),
      });
    }

    // Call original console.error
    originalConsoleError.apply(console, args);
  };

  // Intercept unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const reasonString = String(reason);

    if (
      reasonString.toLowerCase().includes("strategies") ||
      reasonString.toLowerCase().includes("failed to load")
    ) {
      Sentry.captureException(reason || new Error("Unhandled rejection"), {
        tags: {
          errorType: "unhandled_rejection",
          strategy_related: true,
        },
        contexts: {
          promise_rejection: {
            reason: reasonString,
            promise: String(event.promise),
            timestamp: new Date().toISOString(),
          },
        },
      });

      logger.error("🚨 STRATEGY-RELATED UNHANDLED REJECTION", {
        reason: reasonString,
        promise: event.promise,
        timestamp: new Date().toISOString(),
      });
    }
  });

  // Intercept DOM errors (for dynamic error messages)
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;
          const textContent = element.textContent || "";

          // Check if this element contains our error message
          if (textContent.includes("Failed to load strategies")) {
            // Capture the DOM structure
            const errorContext = {
              elementType: element.tagName,
              className: element.className,
              id: element.id,
              parentElement: element.parentElement?.tagName,
              innerHTML: element.innerHTML.substring(0, 500), // Limit size
              timestamp: new Date().toISOString(),
              location: window.location.href,
            };

            Sentry.captureMessage(
              "Failed to load strategies - DOM Error Detected",
              {
                level: "error",
                tags: {
                  component: "dom_mutation",
                  errorType: "strategy_loading",
                },
                contexts: {
                  dom_error: errorContext,
                },
              },
            );

            logger.error("🚨 STRATEGY ERROR FOUND IN DOM", errorContext);

            // Try to find the retry button and add logging
            const retryButton =
              element.querySelector("button") ||
              element.closest("div")?.querySelector("button");

            if (retryButton && retryButton.textContent?.includes("Retry")) {
              const originalOnClick = retryButton.onclick;
              retryButton.onclick = (e) => {
                Sentry.addBreadcrumb({
                  message: "User clicked retry button for strategies",
                  category: "user_action",
                  level: "info",
                  data: {
                    errorMessage: textContent,
                    timestamp: new Date().toISOString(),
                  },
                });

                logger.info("🔄 User clicked retry for strategies", {
                  errorMessage: textContent,
                });

                if (originalOnClick) {
                  return originalOnClick.call(retryButton, e);
                }
              };
            }
          }
        }
      });
    });
  });

  // Start observing the document for changes
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  // Log that interceptor is active
  logger.info("🛡️ Error interceptor initialized", {
    timestamp: new Date().toISOString(),
    location: window.location.href,
  });

  return () => {
    // Cleanup function
    console.error = originalConsoleError;
    observer.disconnect();
  };
}

// Helper to check if any API call might be loading strategies
export function interceptAPIErrors() {
  // Intercept fetch to catch API errors
  const originalFetch = window.fetch;

  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const [input, _init] = args;
    const url = typeof input === "string" ? input : (input as Request).url;

    try {
      const response = await originalFetch(...args);

      // Check if this might be a strategies-related endpoint
      if (url.includes("strateg") || url.includes("optimization")) {
        if (!response.ok) {
          const errorData = {
            url,
            status: response.status,
            statusText: response.statusText,
            headers: Object.fromEntries(response.headers.entries()),
            timestamp: new Date().toISOString(),
          };

          Sentry.captureMessage(
            `Potential strategies API error: ${response.status}`,
            {
              level: "warning",
              tags: {
                component: "api_interceptor",
                endpoint: url,
                status: response.status.toString(),
              },
              contexts: {
                api_error: errorData,
              },
            },
          );

          logger.warn("⚠️ Potential strategies API error", errorData);
        }
      }

      return response;
    } catch (error) {
      // Log any fetch errors that might be strategy-related
      if (url.includes("strateg") || url.includes("optimization")) {
        Sentry.captureException(error, {
          tags: {
            component: "api_interceptor",
            endpoint: url,
            errorType: "fetch_error",
          },
        });

        logger.error("🚨 Strategy API fetch error", {
          url,
          error: String(error),
          timestamp: new Date().toISOString(),
        });
      }

      throw error;
    }
  };
}
